import {
  Border,
  Button,
  CardHeader,
  ChildContainer,
  ColumnRight,
  Container,
  DownArrow,
  IconContainer,
  InitialText,
  Label,
  Row,
  TextContainer,
  Title,
  UpArrow,
} from './ExpandableCard.style';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import Toggle from '../Toggles/Toggle/Toggle';
import { UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { kebabCase } from '../../helpers/kebabCase';
import { memo } from 'preact/compat';
import { useDeviceState } from '../../hooks';
import { useKeyPress } from '../../hooks/useKeyPress';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useUiContext } from '../../contexts/UiContext';

export type ToggleProps = {
  ariaLabel?: string;
  checked: boolean | null;
  defaultChecked?: boolean;
  id: string;
  isDisabled?: boolean;
  label?: string;
  onToggle: (checked: boolean) => void;
};

type Props = {
  ariaLevel: number;
  title: string;
  description?: string;
  toggleDescribedBy?: string;
  hideToggle: boolean;
  label?: JSX.Element;
  onToggleExpandable?: () => void;
  testid?: string;
  toggles?: ToggleProps[];
  useBoldTitle?: boolean;
  isExpandedOnInit?: boolean;
};

const ExpandableCard: FunctionalComponent<Props> = memo(
  ({
    ariaLevel,
    children,
    description,
    label,
    onToggleExpandable,
    title,
    testid = 'uc-expandable-card',
    hideToggle = false,
    toggles,
    useBoldTitle = true,
    isExpandedOnInit = false,
    toggleDescribedBy,
  }) => {
    const [showExpandedContent, setShowExpandedContent] = useState(isExpandedOnInit);
    const [prevShowExpandedContent, setPrevShowExpandedContent] = useState<boolean | null>(null);
    const [skipFocus, setSkipFocus] = useState<boolean>(false);
    const isTabKeyPressed = useKeyPress({ targetKey: 'Tab' });
    const { isMobile } = useDeviceState();
    const { uiVariant, isAmpEnabled } = useUiContext();
    const expandableContentRef = useRef<HTMLDivElement>(null);
    const { labels } = useSettingsContext();
    const isCcpa = uiVariant === UI_VARIANT.CCPA;
    const isTcf = uiVariant === UI_VARIANT.TCF;
    const hasToggle = toggles && toggles.length > 0;

    const checkForInteractibleElements = useCallback(() => {
      const focusableItems = expandableContentRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), video',
      );

      if (focusableItems?.length === 0) {
        setSkipFocus(false);
      }
    }, [expandableContentRef]);

    const onShowExpandableToggle = useCallback(() => {
      setShowExpandedContent(!showExpandedContent);
      setPrevShowExpandedContent(!showExpandedContent);
    }, [showExpandedContent]);

    useEffect(() => {
      if (prevShowExpandedContent != null && onToggleExpandable) {
        onToggleExpandable();
      }
    }, [prevShowExpandedContent, showExpandedContent, onToggleExpandable]);

    useEffect(() => {
      setSkipFocus(showExpandedContent);
      checkForInteractibleElements();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showExpandedContent]);

    useEffect(() => {
      if (showExpandedContent && isTabKeyPressed) {
        setSkipFocus(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabKeyPressed]);

    const expandableCardTitle = kebabCase(title);

    if (!labels) {
      return null;
    }

    return (
      <Container data-testid={testid}>
        <CardHeader hasToggle={hasToggle} hideToggles={isAmpEnabled && !isTcf}>
          <TextContainer
            aria-labelledby={`${expandableCardTitle}-id`}
            hasToggle={hasToggle}
            hideToggles={isAmpEnabled && !isTcf}
            onClick={onShowExpandableToggle}
            aria-expanded={showExpandedContent ? 'true' : 'false'}
            {...((description || toggleDescribedBy) && { 'aria-describedby': `${expandableCardTitle}-description-id` })}
          >
            <Title id={`${expandableCardTitle}-id`} role="heading" aria-level={ariaLevel} useBoldTitle={useBoldTitle}>
              {title}
            </Title>
            {description && <InitialText id={`${expandableCardTitle}-description-id`}>{description}</InitialText>}
            {label && <Label>{label}</Label>}
          </TextContainer>
          <ColumnRight hideToggles={isAmpEnabled && !isTcf}>
            {toggles && (
              <Row reverseDirection={isMobile}>
                {toggles.map((toggleProps) => (
                  <Toggle
                    disableFocus={skipFocus}
                    labelledBy={`${expandableCardTitle}-id`}
                    {...(description && { describedBy: `${expandableCardTitle}-description-id` })}
                    {...(toggleDescribedBy && { describedBy: toggleDescribedBy })}
                    checked={toggleProps.checked === null ? toggleProps.defaultChecked : toggleProps.checked}
                    hideToggle={hideToggle || isCcpa || (isAmpEnabled && !isTcf)}
                    id={`toggle-${toggleProps.id}`}
                    isDisabled={toggleProps.isDisabled}
                    label={toggleProps.label}
                    onToggle={toggleProps.onToggle}
                  />
                ))}
              </Row>
            )}
            <Button
              {...(skipFocus && { tabIndex: -1 })}
              data-testid={`uc-expandable-button-${kebabCase(title.toLowerCase())}`}
              hasToggle={hasToggle}
              hideToggles={isAmpEnabled && !isTcf}
              onClick={onShowExpandableToggle}
              role="button"
              aria-labelledby={`${expandableCardTitle}-id`}
              {...(description && { 'aria-describedby': `${expandableCardTitle}-description-id` })}
              {...(toggleDescribedBy && { 'aria-describedby': `${expandableCardTitle}-description-id` })}
              aria-expanded={showExpandedContent ? 'true' : 'false'}
            >
              <IconContainer>{showExpandedContent ? <UpArrow /> : <DownArrow />}</IconContainer>
            </Button>
          </ColumnRight>
        </CardHeader>

        {showExpandedContent && (
          <Fragment>
            <Border />
            <ChildContainer ref={expandableContentRef} data-testid="uc-expandable-content">
              {children}
            </ChildContainer>
          </Fragment>
        )}
      </Container>
    );
  },
);

export default ExpandableCard;
